import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpContext } from '@angular/common/http';

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
    encodeKey(k) {
        return encodeURIComponent(k);
    }
    encodeValue(v) {
        return encodeURIComponent(v);
    }
    decodeKey(k) {
        return decodeURIComponent(k);
    }
    decodeValue(v) {
        return decodeURIComponent(v);
    }
}

const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
    'csv': ',',
    'tsv': '   ',
    'ssv': ' ',
    'pipes': '|'
};

class PortfolioAnalyticsConfiguration {
    constructor(configurationParameters = {}) {
        this.apiKeys = configurationParameters.apiKeys;
        this.username = configurationParameters.username;
        this.password = configurationParameters.password;
        this.accessToken = configurationParameters.accessToken;
        this.basePath = configurationParameters.basePath;
        this.withCredentials = configurationParameters.withCredentials;
        this.encoder = configurationParameters.encoder;
        if (configurationParameters.encodeParam) {
            this.encodeParam = configurationParameters.encodeParam;
        }
        else {
            this.encodeParam = param => this.defaultEncodeParam(param);
        }
        if (configurationParameters.credentials) {
            this.credentials = configurationParameters.credentials;
        }
        else {
            this.credentials = {};
        }
    }
    /**
     * Select the correct content-type to use for a request.
     * Uses {@link PortfolioAnalyticsConfiguration#isJsonMime} to determine the correct content-type.
     * If no content type is found return the first found type if the contentTypes is not empty
     * @param contentTypes - the array of content types that are available for selection
     * @returns the selected content-type or <code>undefined</code> if no selection could be made.
     */
    selectHeaderContentType(contentTypes) {
        if (contentTypes.length === 0) {
            return undefined;
        }
        const type = contentTypes.find((x) => this.isJsonMime(x));
        if (type === undefined) {
            return contentTypes[0];
        }
        return type;
    }
    /**
     * Select the correct accept content-type to use for a request.
     * Uses {@link PortfolioAnalyticsConfiguration#isJsonMime} to determine the correct accept content-type.
     * If no content type is found return the first found type if the contentTypes is not empty
     * @param accepts - the array of content types that are available for selection.
     * @returns the selected content-type or <code>undefined</code> if no selection could be made.
     */
    selectHeaderAccept(accepts) {
        if (accepts.length === 0) {
            return undefined;
        }
        const type = accepts.find((x) => this.isJsonMime(x));
        if (type === undefined) {
            return accepts[0];
        }
        return type;
    }
    /**
     * Check if the given MIME is a JSON MIME.
     * JSON MIME examples:
     *   application/json
     *   application/json; charset=UTF8
     *   APPLICATION/JSON
     *   application/vnd.company+json
     * @param mime - MIME (Multipurpose Internet Mail Extensions)
     * @return True if the given MIME is JSON, false otherwise.
     */
    isJsonMime(mime) {
        const jsonMime = new RegExp('^(application\/json|[^;/ \t]+\/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
        return mime !== null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
    }
    lookupCredential(key) {
        const value = this.credentials[key];
        return typeof value === 'function'
            ? value()
            : value;
    }
    defaultEncodeParam(param) {
        // This implementation exists as fallback for missing configuration
        // and for backwards compatibility to older typescript-angular generator versions.
        // It only works for the 'simple' parameter style.
        // Date-handling only works for the 'date-time' format.
        // All other styles and Date-formats are probably handled incorrectly.
        //
        // But: if that's all you need (i.e.: the most common use-case): no need for customization!
        const value = param.dataFormat === 'date-time' && param.value instanceof Date
            ? param.value.toISOString()
            : param.value;
        return encodeURIComponent(String(value));
    }
}

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
class PortfolioAnalyticsApiService {
    constructor(httpClient, basePath, configuration) {
        this.httpClient = httpClient;
        this.basePath = 'http://localhost/api/wealth/distribution/analytics/v1';
        this.defaultHeaders = new HttpHeaders();
        this.configuration = new PortfolioAnalyticsConfiguration();
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (Array.isArray(basePath) && basePath.length > 0) {
                basePath = basePath[0];
            }
            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        }
        else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
        if (value == null) {
            return httpParams;
        }
        if (typeof value === "object") {
            if (Array.isArray(value)) {
                value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            }
            else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
                }
                else {
                    throw Error("key may not be null if value is Date");
                }
            }
            else {
                Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
            }
        }
        else if (key != null) {
            httpParams = httpParams.append(key, value);
        }
        else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }
    portfolioAnalyticsAPIComputePortfolioAnalytics(vNDComBlackrockRequestID, vNDComBlackrockOriginTimestamp, body, observe = 'body', reportProgress = false, options) {
        if (vNDComBlackrockRequestID === null || vNDComBlackrockRequestID === undefined) {
            throw new Error('Required parameter vNDComBlackrockRequestID was null or undefined when calling portfolioAnalyticsAPIComputePortfolioAnalytics.');
        }
        if (vNDComBlackrockOriginTimestamp === null || vNDComBlackrockOriginTimestamp === undefined) {
            throw new Error('Required parameter vNDComBlackrockOriginTimestamp was null or undefined when calling portfolioAnalyticsAPIComputePortfolioAnalytics.');
        }
        if (body === null || body === undefined) {
            throw new Error('Required parameter body was null or undefined when calling portfolioAnalyticsAPIComputePortfolioAnalytics.');
        }
        let localVarHeaders = this.defaultHeaders;
        if (vNDComBlackrockRequestID !== undefined && vNDComBlackrockRequestID !== null) {
            localVarHeaders = localVarHeaders.set('VND.com.blackrock.Request-ID', String(vNDComBlackrockRequestID));
        }
        if (vNDComBlackrockOriginTimestamp !== undefined && vNDComBlackrockOriginTimestamp !== null) {
            localVarHeaders = localVarHeaders.set('VND.com.blackrock.Origin-Timestamp', String(vNDComBlackrockOriginTimestamp));
        }
        let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }
        let localVarHttpContext = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }
        // to determine the Content-Type header
        const consumes = [
            'application/json'
        ];
        const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
        }
        let responseType_ = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            }
            else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            }
            else {
                responseType_ = 'blob';
            }
        }
        let localVarPath = `/portfolios:computePortfolioAnalytics`;
        return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
            context: localVarHttpContext,
            body: body,
            responseType: responseType_,
            withCredentials: this.configuration.withCredentials,
            headers: localVarHeaders,
            observe: observe,
            reportProgress: reportProgress
        });
    }
}
PortfolioAnalyticsApiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortfolioAnalyticsApiService, deps: [{ token: i1.HttpClient }, { token: BASE_PATH, optional: true }, { token: PortfolioAnalyticsConfiguration, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
PortfolioAnalyticsApiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortfolioAnalyticsApiService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortfolioAnalyticsApiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [BASE_PATH]
                }] }, { type: PortfolioAnalyticsConfiguration, decorators: [{
                    type: Optional
                }] }]; } });

const APIS = [PortfolioAnalyticsApiService];

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const EnumsOrigin = {
    Unspecified: 'ORIGIN_UNSPECIFIED',
    System: 'ORIGIN_SYSTEM',
    User: 'ORIGIN_USER'
};

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const V1Capfloor = {
    Unspecified: 'CAPFLOOR_UNSPECIFIED',
    Percent: 'CAPFLOOR_PERCENT',
    Monetary: 'CAPFLOOR_MONETARY'
};

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const V1EventType = {
    Unspecified: 'EVENT_TYPE_UNSPECIFIED',
    Entry: 'EVENT_TYPE_ENTRY',
    Exit: 'EVENT_TYPE_EXIT',
    AnuallyRecurring: 'EVENT_TYPE_ANUALLY_RECURRING'
};

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const V1ExPostPeriod = {
    Unspecified: 'EX_POST_PERIOD_UNSPECIFIED',
    OneYear: 'EX_POST_PERIOD_ONE_YEAR',
    ThreeYear: 'EX_POST_PERIOD_THREE_YEAR',
    FiveYear: 'EX_POST_PERIOD_FIVE_YEAR',
    SevenYear: 'EX_POST_PERIOD_SEVEN_YEAR',
    TenYear: 'EX_POST_PERIOD_TEN_YEAR',
    Total: 'EX_POST_PERIOD_TOTAL',
    Custom: 'EX_POST_PERIOD_CUSTOM'
};

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const V1ExPostType = {
    Unspecified: 'EX_POST_TYPE_UNSPECIFIED',
    Trr: 'EX_POST_TYPE_TRR',
    TrrAnn: 'EX_POST_TYPE_TRR_ANN',
    StdDev: 'EX_POST_TYPE_STD_DEV',
    SharpeRatio: 'EX_POST_TYPE_SHARPE_RATIO',
    Beta: 'EX_POST_TYPE_BETA',
    Alpha: 'EX_POST_TYPE_ALPHA',
    MaxDrawdown: 'EX_POST_TYPE_MAX_DRAWDOWN',
    UpMonth: 'EX_POST_TYPE_UP_MONTH',
    UpMonthPct: 'EX_POST_TYPE_UP_MONTH_PCT',
    DownMonth: 'EX_POST_TYPE_DOWN_MONTH',
    DownMonthPct: 'EX_POST_TYPE_DOWN_MONTH_PCT',
    Max: 'EX_POST_TYPE_MAX',
    Min: 'EX_POST_TYPE_MIN',
    UpCaptureRatio: 'EX_POST_TYPE_UP_CAPTURE_RATIO',
    DownCaptureRatio: 'EX_POST_TYPE_DOWN_CAPTURE_RATIO',
    Kurtosis: 'EX_POST_TYPE_KURTOSIS',
    BattingAverage: 'EX_POST_TYPE_BATTING_AVERAGE',
    TreynorRatio: 'EX_POST_TYPE_TREYNOR_RATIO',
    Mean: 'EX_POST_TYPE_MEAN',
    Correlation: 'EX_POST_TYPE_CORRELATION',
    RSquared: 'EX_POST_TYPE_R_SQUARED',
    Modigliani: 'EX_POST_TYPE_MODIGLIANI',
    InfoRatio: 'EX_POST_TYPE_INFO_RATIO',
    BestRunup: 'EX_POST_TYPE_BEST_RUNUP',
    WorstRundown: 'EX_POST_TYPE_WORST_RUNDOWN',
    AlphaTstat: 'EX_POST_TYPE_ALPHA_TSTAT',
    BetaTstat: 'EX_POST_TYPE_BETA_TSTAT'
};

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const V1PaymentMethod = {
    Unspecified: 'PAYMENT_METHOD_UNSPECIFIED',
    Percent: 'PAYMENT_METHOD_PERCENT',
    Monetary: 'PAYMENT_METHOD_MONETARY'
};

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const V1PercentMethod = {
    Unspecified: 'PERCENT_METHOD_UNSPECIFIED',
    Progressive: 'PERCENT_METHOD_PROGRESSIVE',
    Flat: 'PERCENT_METHOD_FLAT'
};

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const V1PortfolioAnalyticType = {
    Unspecified: 'PORTFOLIO_ANALYTIC_TYPE_UNSPECIFIED',
    Risk: 'PORTFOLIO_ANALYTIC_TYPE_RISK',
    TailRisk: 'PORTFOLIO_ANALYTIC_TYPE_TAIL_RISK',
    Leverage: 'PORTFOLIO_ANALYTIC_TYPE_LEVERAGE',
    Scenarios: 'PORTFOLIO_ANALYTIC_TYPE_SCENARIOS',
    Breakdowns: 'PORTFOLIO_ANALYTIC_TYPE_BREAKDOWNS',
    Characteristics: 'PORTFOLIO_ANALYTIC_TYPE_CHARACTERISTICS',
    Esg: 'PORTFOLIO_ANALYTIC_TYPE_ESG',
    SimulatedPerformance: 'PORTFOLIO_ANALYTIC_TYPE_SIMULATED_PERFORMANCE',
    WealthProjections: 'PORTFOLIO_ANALYTIC_TYPE_WEALTH_PROJECTIONS',
    Nav: 'PORTFOLIO_ANALYTIC_TYPE_NAV',
    Rules: 'PORTFOLIO_ANALYTIC_TYPE_RULES',
    Scores: 'PORTFOLIO_ANALYTIC_TYPE_SCORES',
    CustomAnalytics: 'PORTFOLIO_ANALYTIC_TYPE_CUSTOM_ANALYTICS',
    CreditRisk: 'PORTFOLIO_ANALYTIC_TYPE_CREDIT_RISK',
    RiskPositionContrib: 'PORTFOLIO_ANALYTIC_TYPE_RISK_POSITION_CONTRIB',
    RiskFactorContrib: 'PORTFOLIO_ANALYTIC_TYPE_RISK_FACTOR_CONTRIB',
    RiskFactorPositionContrib: 'PORTFOLIO_ANALYTIC_TYPE_RISK_FACTOR_POSITION_CONTRIB',
    IdioPositionContrib: 'PORTFOLIO_ANALYTIC_TYPE_IDIO_POSITION_CONTRIB'
};

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * Wealth Distribution - Portfolio-Analytics
 * API For Portfolio Analysis.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
const Wealthdistributionassetv1AssetIdentifierType = {
    Unspecified: 'ASSET_IDENTIFIER_TYPE_UNSPECIFIED',
    ClientId: 'ASSET_IDENTIFIER_TYPE_CLIENT_ID',
    Bcusip: 'ASSET_IDENTIFIER_TYPE_BCUSIP',
    Cusip: 'ASSET_IDENTIFIER_TYPE_CUSIP',
    Sedol: 'ASSET_IDENTIFIER_TYPE_SEDOL',
    Isin: 'ASSET_IDENTIFIER_TYPE_ISIN',
    Cins: 'ASSET_IDENTIFIER_TYPE_CINS',
    Ric: 'ASSET_IDENTIFIER_TYPE_RIC',
    Wkn: 'ASSET_IDENTIFIER_TYPE_WKN',
    Valor: 'ASSET_IDENTIFIER_TYPE_VALOR',
    MorningstarSecId: 'ASSET_IDENTIFIER_TYPE_MORNINGSTAR_SEC_ID',
    Ticker: 'ASSET_IDENTIFIER_TYPE_TICKER',
    ModelId: 'ASSET_IDENTIFIER_TYPE_MODEL_ID'
};

class PortfolioAnalyticsApiModule {
    constructor(parentModule, http) {
        if (parentModule) {
            throw new Error('PortfolioAnalyticsApiModule is already loaded. Import in your base AppModule only.');
        }
        if (!http) {
            throw new Error('You need to import the HttpClientModule in your AppModule! \n' +
                'See also https://github.com/angular/angular/issues/20575');
        }
    }
    static forRoot(configurationFactory) {
        return {
            ngModule: PortfolioAnalyticsApiModule,
            providers: [{ provide: PortfolioAnalyticsConfiguration, useFactory: configurationFactory }]
        };
    }
}
PortfolioAnalyticsApiModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortfolioAnalyticsApiModule, deps: [{ token: PortfolioAnalyticsApiModule, optional: true, skipSelf: true }, { token: i1.HttpClient, optional: true }], target: i0.ɵɵFactoryTarget.NgModule });
PortfolioAnalyticsApiModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.12", ngImport: i0, type: PortfolioAnalyticsApiModule });
PortfolioAnalyticsApiModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortfolioAnalyticsApiModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.12", ngImport: i0, type: PortfolioAnalyticsApiModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [],
                    exports: [],
                    providers: []
                }]
        }], ctorParameters: function () { return [{ type: PortfolioAnalyticsApiModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: i1.HttpClient, decorators: [{
                    type: Optional
                }] }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, BASE_PATH, COLLECTION_FORMATS, EnumsOrigin, PortfolioAnalyticsApiModule, PortfolioAnalyticsApiService, PortfolioAnalyticsConfiguration, V1Capfloor, V1EventType, V1ExPostPeriod, V1ExPostType, V1PaymentMethod, V1PercentMethod, V1PortfolioAnalyticType, Wealthdistributionassetv1AssetIdentifierType };
